/**
 * Cash, drawers and shifts.
 *
 * Specification: ../test/shift.spec.ts. Master plan §4 M8.
 *
 * A small module with a disproportionate effect on whether anyone trusts the
 * system, and the one that justifies the append-only journal: almost every
 * cash theft requires a POS action to conceal it, so the detection surface is
 * the mutation log rather than the cash count.
 *
 * Four decisions carry it:
 *
 *   - ROUNDING IS PART OF THE EXPECTED DRAWER. Czech cash rounds to whole
 *     koruna; the difference is not revenue but it IS physically in the till.
 *     Omit it and every honest cashier looks a few koruna short every day, in
 *     the same direction, which is exactly what skimming small change looks
 *     like. The control then loses credibility and nobody investigates the
 *     shift where it mattered.
 *   - BLIND COUNT IS ENFORCED HERE, not in the UI. `countSheetFor` has no way
 *     to express the expectation, so no payload built from it can leak one.
 *   - THRESHOLDS ARE ABSOLUTE AND RELATIVE. 200 CZK short on 2 000 is a
 *     different event from 200 CZK on 80 000.
 *   - THE EXPECTATION IS DERIVED FROM THE LOG. archive/v0 shipped a
 *     client-computed accumulator in the close payload, which made every shift
 *     report irreproducible the moment anyone asked about one.
 */

// --- Denominations ---------------------------------------------------------

/**
 * Czech currency in circulation, in haléře: 1/2/5/10/20/50 Kč coins and
 * 100/200/500/1000/2000/5000 Kč notes.
 *
 * Ascending, and the order is load-bearing: it is the order of the count sheet,
 * and a stable sheet is what lets a cashier count the same way every night.
 */
export const CZK_DENOMINATIONS = [
  100, 200, 500, 1_000, 2_000, 5_000, 10_000, 20_000, 50_000, 100_000, 200_000, 500_000,
] as const;

export interface DenominationCount {
  readonly denominationMinor: number;
  readonly count: number;
}

export function countTotal(counts: readonly DenominationCount[]): bigint {
  return counts.reduce(
    (acc, c) => acc + BigInt(c.denominationMinor) * BigInt(c.count),
    0n,
  );
}

// --- Model -----------------------------------------------------------------

/**
 * All three are real and a venue picks one per drawer.
 *
 * SERVER_BANKING is the one Anglo products usually omit and Czech table
 * service usually wants: each server carries their own bank and settles net,
 * so the control is a settlement record rather than a drawer count.
 */
export type DrawerModel = "IMPREST" | "SHARED_TILL" | "SERVER_BANKING";

export type CashMovementKind = "PAY_IN" | "PAY_OUT" | "SAFE_DROP" | "TIP_OUT";

export type CountKind = "MID_SHIFT" | "CLOSE";

export interface CashMovement {
  readonly movementId: string;
  readonly kind: CashMovementKind;
  readonly amountMinor: bigint;
  readonly reasonCode: string;
  readonly at: Date;
  readonly actorId: string;
  readonly bagId?: string;
  readonly witnessedBy?: string;
}

export interface CountRecord {
  readonly countId: string;
  readonly kind: CountKind;
  readonly denominations: readonly DenominationCount[];
  readonly countedBy: string;
  readonly at: Date;
}

export type ShiftAnomalyKind =
  | "ALREADY_OPEN"
  | "SHIFT_NOT_OPEN"
  | "MOVEMENT_WITHOUT_REASON"
  | "SAFE_DROP_UNWITNESSED"
  | "NEGATIVE_AMOUNT"
  | "NO_CLOSING_COUNT";

export interface ShiftAnomaly {
  readonly kind: ShiftAnomalyKind;
  readonly eventId: string;
  readonly detail: string;
}

interface Envelope {
  readonly eventId: string;
  readonly occurredAt: Date;
  readonly actorId: string;
}

export type ShiftEvent =
  | (Envelope & {
      type: "shift.opened";
      payload: {
        shiftId: string;
        businessDate: string;
        drawerModel: DrawerModel;
        drawerId: string;
        cashierId: string;
        openingFloat: readonly DenominationCount[];
      };
    })
  | (Envelope & {
      type: "cash.moved";
      payload: {
        movementId: string;
        kind: CashMovementKind;
        amountMinor: bigint;
        reasonCode: string;
        bagId?: string;
        witnessedBy?: string;
      };
    })
  | (Envelope & {
      type: "shift.counted";
      payload: {
        countId: string;
        kind: CountKind;
        denominations: readonly DenominationCount[];
        countedBy: string;
      };
    })
  | (Envelope & { type: "shift.closed"; payload: { shiftId: string } });

export interface ShiftState {
  readonly shiftId: string;
  readonly businessDate: string;
  readonly drawerModel: DrawerModel;
  readonly drawerId: string;
  readonly cashierId: string;
  readonly openingFloat: readonly DenominationCount[];
  readonly movements: readonly CashMovement[];
  /** Append-only. A recount is a new record; the first one is evidence. */
  readonly counts: readonly CountRecord[];
  readonly openedAt: Date;
  readonly closedAt: Date | null;
  readonly anomalies: readonly ShiftAnomaly[];
  readonly appliedEventIds: ReadonlySet<string>;
}

// --- The reducer -----------------------------------------------------------

interface Draft {
  shiftId: string;
  businessDate: string;
  drawerModel: DrawerModel;
  drawerId: string;
  cashierId: string;
  openingFloat: readonly DenominationCount[];
  movements: CashMovement[];
  counts: CountRecord[];
  openedAt: Date;
  closedAt: Date | null;
  anomalies: ShiftAnomaly[];
  appliedEventIds: Set<string>;
}

export function foldShift(events: readonly ShiftEvent[]): ShiftState | undefined {
  let d: Draft | undefined;

  for (const event of events) {
    if (d?.appliedEventIds.has(event.eventId)) continue;

    if (event.type === "shift.opened") {
      if (d) {
        d.anomalies.push({
          kind: "ALREADY_OPEN",
          eventId: event.eventId,
          detail: `shift ${d.shiftId} is already open`,
        });
        d.appliedEventIds.add(event.eventId);
        continue;
      }
      const p = event.payload;
      d = {
        shiftId: p.shiftId,
        businessDate: p.businessDate,
        drawerModel: p.drawerModel,
        drawerId: p.drawerId,
        cashierId: p.cashierId,
        openingFloat: [...p.openingFloat],
        movements: [],
        counts: [],
        openedAt: event.occurredAt,
        closedAt: null,
        anomalies: [],
        appliedEventIds: new Set([event.eventId]),
      };
      continue;
    }

    if (!d) continue;
    applyToShift(d, event);
    d.appliedEventIds.add(event.eventId);
  }

  return d ? { ...d } : undefined;
}

function applyToShift(d: Draft, event: ShiftEvent): void {
  const fail = (kind: ShiftAnomalyKind, detail: string): void => {
    d.anomalies.push({ kind, eventId: event.eventId, detail });
  };

  // A closed shift is a closed book. A correction after close is a new,
  // attributed adjustment against the closed shift — never a quiet edit of it.
  if (d.closedAt !== null) {
    fail("SHIFT_NOT_OPEN", `shift ${d.shiftId} closed at ${d.closedAt.toISOString()}`);
    return;
  }

  switch (event.type) {
    case "shift.opened":
      fail("ALREADY_OPEN", `shift ${d.shiftId} is already open`);
      return;

    case "cash.moved": {
      const p = event.payload;
      if (d.movements.some((m) => m.movementId === p.movementId)) return;

      if (p.amountMinor <= 0n) {
        // Direction is carried by `kind`, never by a sign. A negative pay-out
        // is a pay-in with worse provenance.
        fail("NEGATIVE_AMOUNT", `movement ${p.movementId} must be positive; kind carries direction`);
        return;
      }
      if (!p.reasonCode.trim()) {
        // Reason codes are what make a drawer-open pattern legible at all.
        fail("MOVEMENT_WITHOUT_REASON", `movement ${p.movementId} has no reason code`);
        return;
      }
      if (p.kind === "SAFE_DROP" && (!p.bagId?.trim() || !p.witnessedBy?.trim())) {
        // A drop that cannot be reconciled against tomorrow's bank credit is
        // not a control, it is a hole with paperwork attached.
        fail(
          "SAFE_DROP_UNWITNESSED",
          `safe drop ${p.movementId} needs both a bag id and a witness so it can be matched ` +
            `against the deposit`,
        );
        return;
      }

      d.movements.push({
        movementId: p.movementId,
        kind: p.kind,
        amountMinor: p.amountMinor,
        reasonCode: p.reasonCode,
        at: event.occurredAt,
        actorId: event.actorId,
        ...(p.bagId ? { bagId: p.bagId } : {}),
        ...(p.witnessedBy ? { witnessedBy: p.witnessedBy } : {}),
      });
      return;
    }

    case "shift.counted": {
      const p = event.payload;
      if (d.counts.some((c) => c.countId === p.countId)) return;
      // Appended, never replaced. Overwriting a first count destroys the
      // record that shows someone recounted until the number came out right.
      d.counts.push({
        countId: p.countId,
        kind: p.kind,
        denominations: [...p.denominations],
        countedBy: p.countedBy,
        at: event.occurredAt,
      });
      return;
    }

    case "shift.closed": {
      if (!d.counts.some((c) => c.kind === "CLOSE")) {
        fail(
          "NO_CLOSING_COUNT",
          `shift ${d.shiftId} has no closing count; there is nothing to reconcile the drawer ` +
            `against and the variance would be unknowable`,
        );
        return;
      }
      d.closedAt = event.occurredAt;
      return;
    }
  }
}

// --- The expectation -------------------------------------------------------

/**
 * Cash facts summarised from the ORDER stream for this shift.
 *
 * Passed in rather than joined, because orders are another module's tables and
 * the boundary is the point (D12). The caller assembles these from the orders
 * projection.
 */
export interface CashSalesFacts {
  readonly cashSalesMinor: bigint;
  readonly cashRefundsMinor: bigint;
  /**
   * Net cash rounding across the shift's cash tenders. Signed.
   *
   * Non-taxable, not revenue, and physically in the drawer. See the module
   * header for why leaving this out quietly discredits the whole control.
   */
  readonly cashRoundingMinor: bigint;
  /**
   * Tips handed over in cash. In the drawer until tipped out — not revenue,
   * and not a sale, so itemised on the Z rather than folded into sales.
   * Absent is none.
   */
  readonly cashTipsMinor?: bigint;
}

export interface DrawerBreakdown {
  readonly openingFloatMinor: bigint;
  readonly cashSalesMinor: bigint;
  readonly cashRefundsMinor: bigint;
  readonly roundingMinor: bigint;
  readonly cashTipsMinor: bigint;
  readonly payInsMinor: bigint;
  readonly payOutsMinor: bigint;
  readonly safeDropsMinor: bigint;
  readonly tipOutsMinor: bigint;
}

export interface DrawerExpectation {
  readonly expectedMinor: bigint;
  /** Every term, so a variance conversation is about arithmetic, not trust. */
  readonly breakdown: DrawerBreakdown;
}

export function expectedDrawer(shift: ShiftState, sales: CashSalesFacts): DrawerExpectation {
  const sum = (kind: CashMovementKind): bigint =>
    shift.movements
      .filter((m) => m.kind === kind)
      .reduce((acc, m) => acc + m.amountMinor, 0n);

  const breakdown: DrawerBreakdown = {
    openingFloatMinor: countTotal(shift.openingFloat),
    cashSalesMinor: sales.cashSalesMinor,
    cashRefundsMinor: sales.cashRefundsMinor,
    roundingMinor: sales.cashRoundingMinor,
    cashTipsMinor: sales.cashTipsMinor ?? 0n,
    payInsMinor: sum("PAY_IN"),
    payOutsMinor: sum("PAY_OUT"),
    safeDropsMinor: sum("SAFE_DROP"),
    tipOutsMinor: sum("TIP_OUT"),
  };

  const expectedMinor =
    breakdown.openingFloatMinor +
    breakdown.cashSalesMinor -
    breakdown.cashRefundsMinor +
    breakdown.roundingMinor +
    breakdown.cashTipsMinor +
    breakdown.payInsMinor -
    breakdown.payOutsMinor -
    breakdown.safeDropsMinor -
    breakdown.tipOutsMinor;

  return { expectedMinor, breakdown };
}

// --- Blind counting --------------------------------------------------------

export interface CountSheetLine {
  readonly denominationMinor: number;
  /** Always zero on issue. Pre-filling from the float would be a hint. */
  readonly count: number;
}

/**
 * What the counting device receives.
 *
 * Note what this type cannot express: any expectation, any variance, any prior
 * count. The blind guarantee is therefore structural rather than a promise
 * about rendering — a payload built from this simply has nothing to leak, and
 * a curious cashier with dev tools open learns only the denominations they can
 * already see in the drawer.
 */
export interface CountSheet {
  readonly shiftId: string;
  readonly kind: CountKind;
  readonly lines: readonly CountSheetLine[];
}

export function countSheetFor(shift: ShiftState, kind: CountKind): CountSheet {
  return {
    shiftId: shift.shiftId,
    kind,
    lines: CZK_DENOMINATIONS.map((denominationMinor) => ({ denominationMinor, count: 0 })),
  };
}

// --- Variance --------------------------------------------------------------

export interface VariancePolicy {
  /** Below this, nothing is raised however large the percentage. */
  readonly watchAbsoluteMinor: bigint;
  readonly alertAbsoluteMinor: bigint;
  readonly watchBp: number;
  readonly alertBp: number;
}

export type VarianceSeverity = "OK" | "WATCH" | "ALERT";
export type VarianceDirection = "OVER" | "SHORT" | "EXACT";

export interface VarianceAssessment {
  readonly varianceMinor: bigint;
  readonly direction: VarianceDirection;
  readonly severity: VarianceSeverity;
  /** Null when there was nothing to be a proportion of. */
  readonly relativeBp: number | null;
}

/**
 * Judge a drawer variance on both scales.
 *
 * The absolute is a NOISE FLOOR and the relative is the escalator: 1 CZK out
 * on a 50 CZK drawer is 2 % and is somebody fumbling change, while 500 CZK is
 * 500 CZK even against a million.
 *
 * Consistent OVERS are raised as loudly as shorts, which surprises people. An
 * over is frequently a failed skim — cash taken from the till and not yet
 * removed, or a sale rung short with the difference pocketed later.
 */
export function classifyVariance(
  varianceMinor: bigint,
  expectedMinor: bigint,
  policy: VariancePolicy,
): VarianceAssessment {
  const magnitude = varianceMinor < 0n ? -varianceMinor : varianceMinor;
  const direction: VarianceDirection =
    varianceMinor === 0n ? "EXACT" : varianceMinor > 0n ? "OVER" : "SHORT";

  const relativeBp =
    expectedMinor > 0n ? Number((magnitude * 10_000n) / expectedMinor) : null;

  if (magnitude < policy.watchAbsoluteMinor) {
    return { varianceMinor, direction, severity: "OK", relativeBp };
  }
  const severity: VarianceSeverity =
    magnitude >= policy.alertAbsoluteMinor ||
    (relativeBp !== null && relativeBp >= policy.alertBp)
      ? "ALERT"
      : "WATCH";

  return { varianceMinor, direction, severity, relativeBp };
}

// --- Prompts ---------------------------------------------------------------

/**
 * Time to move cash to the safe.
 *
 * Loss prevention and staff safety at once: a till holding 15 000 CZK on a
 * Friday night is a robbery risk as much as a shrinkage one.
 */
export function shouldPromptSafeDrop(drawerCashMinor: bigint, ceilingMinor: bigint): boolean {
  return drawerCashMinor > ceilingMinor;
}

/** FNV-1a, 32-bit. Cheap, well-distributed, and identical on every replica. */
function hash32(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Should this be a surprise count?
 *
 * The deterrent value of a mid-shift count comes ENTIRELY from being
 * unpredictable — a count at 15:00 every day is a count a dishonest cashier
 * plans around. But it must also be reproducible, or nobody can later show
 * that the prompt was actually presented and declined.
 *
 * Hashing (shiftId, n) gives both: unguessable from the clock, identical on
 * every replica, and reconstructible months later.
 */
export function shouldPromptCount(shiftId: string, sequence: number, rateBp: number): boolean {
  if (rateBp <= 0) return false;
  if (rateBp >= 10_000) return true;
  return hash32(`${shiftId}#${sequence}`) % 10_000 < rateBp;
}
